import React, { useRef, useState, useEffect, useCallback } from 'react';
import './BaslikPanel.css';
import { usePanels } from '../../../context/PanelsContext';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectBaslikDefaults,
  selectSelectedCell,
  selectCellLocks,
  selectCellFormats,
  updateBaslikDefaults,
  setSelectedCell,
  toggleCellLock,
  updateCellFormat,
} from '../../../redux/baslikSlice';
import { BASLIK_SECTIONS } from '../../../config/baslikConfig/defaults';
import {
  selectTemplates,
  selectActiveTemplateId,
  selectActiveTemplate,
  setActiveTemplate,
  openEditor,
  loadTemplateForEdit,
  deleteTemplate,
} from '../../../redux/baslikTemplateSlice';
import { fetchDefaultBaslikId, saveDefaultBaslikId, fetchBaslikTemplateList, applyBaslikTemplate } from '../../../services/baslikDefault';
import { useStore } from 'react-redux';
import { resolveBaslikAutofill } from '../../../config/baslikConfig/variableCatalog';

const BaslikPanel = ({ scene }) => {
  const dispatch = useDispatch();
  const baslikDefaults = useSelector(selectBaslikDefaults);
  const selectedCell = useSelector(selectSelectedCell);
  const cellLocks = useSelector(selectCellLocks);
  const cellFormats = useSelector(selectCellFormats);
  const templates = useSelector(selectTemplates);
  const activeTemplateId = useSelector(selectActiveTemplateId);
  const activeTemplate = useSelector(selectActiveTemplate);
  const store = useStore();

  // ─── Varsayılan başlık (hesaba bağlı) ──────────────
  const [defaultBaslikId, setDefaultBaslikId] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  useEffect(() => { fetchDefaultBaslikId().then(setDefaultBaslikId); }, []);
  useEffect(() => { fetchBaslikTemplateList().then(setAllTemplates); }, []);

  // ─── Otomatik doldurma: BOŞ başlık alanlarını hesaptan + projeden + supply'dan
  //     doldur. Sadece boş alanlara yazar (kullanıcı girdisini/dolu alanı EZMEZ);
  //     başka panel özelliği değişmez. Proje metadata'sı veya supply değişince yeniden dener.
  const projectMetadata = useSelector(s => s.project?.projectMetadata);
  const supplyComponents = useSelector(s => s.simpleSupply?.components);
  useEffect(() => {
    const fill = resolveBaslikAutofill(store.getState());
    const sections = Object.keys(fill);
    if (sections.length === 0) return; // doldurulacak boş alan yok
    sections.forEach(section => {
      dispatch(updateBaslikDefaults({ section, changes: fill[section] }));
    });
  }, [projectMetadata, supplyComponents, dispatch, store]);
  // Dropdown listesi: backend'deki tüm şablonlar + (varsa) listede olmayan aktif şablon
  const dropdownTemplates = (activeTemplateId && !allTemplates.some(t => t.id === activeTemplateId))
    ? [...allTemplates, { id: activeTemplateId, name: activeTemplate?.name || 'Aktif şablon' }]
    : allTemplates;
  const handleVarsayilanYap = useCallback(async () => {
    if (!activeTemplateId) return;
    try {
      const id = await saveDefaultBaslikId(activeTemplateId);
      setDefaultBaslikId(id);
    } catch (e) {
      alert('Varsayılan kaydedilemedi: ' + (e?.message || e));
    }
  }, [activeTemplateId]);

  // ─── Akordion ──────────────────────────────────────
  const [openSections, setOpenSections] = useState(() => {
    const map = {};
    BASLIK_SECTIONS.forEach((s) => { map[s.sectionKey] = true; });
    return map;
  });
  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ─── Handlers ──────────────────────────────────────
  // Not: Başlık artık proje layout'ı (useElectricalLayout) tarafından otomatik
  // çizilir/güncellenir; panel alanları değişince başlık sahnede canlı yenilenir.
  // Manuel "Başlık Oluştur"/"Temizle" düğmeleri kaldırıldı (çift çizim + istemsiz
  // silme sorunlarına yol açıyordu).
  const handleYeniSablon = () => { dispatch(openEditor()); };
  const handleSablonDuzenle = () => {
    if (activeTemplateId) dispatch(loadTemplateForEdit(activeTemplateId));
  };
  const handleSablonSil = () => {
    if (activeTemplateId) dispatch(deleteTemplate(activeTemplateId));
  };
  const handleSablonChange = async (e) => {
    const val = e.target.value;
    if (!val) { dispatch(setActiveTemplate(null)); return; }
    // Seçilen şablonun içeriğini backend'den çek + aktive et (yoksa boş görünür)
    const ok = await applyBaslikTemplate(val);
    if (!ok) alert('Şablon yüklenemedi.');
  };

  const handleCellClick = useCallback((cellId) => {
    dispatch(setSelectedCell(cellId));
  }, [dispatch]);

  const handleLockToggle = useCallback((e, cellId) => {
    e.stopPropagation();
    dispatch(toggleCellLock(cellId));
    dispatch(setSelectedCell(cellId));
  }, [dispatch]);

  const handleValueChange = useCallback((sectionKey, fieldKey, value) => {
    dispatch(updateBaslikDefaults({ section: sectionKey, changes: { [fieldKey]: value } }));
  }, [dispatch]);

  // ─── Biçim barı ────────────────────────────────────
  const selectedFormat = selectedCell ? (cellFormats[selectedCell] || {}) : {};
  const currentAlign = selectedFormat.align || 'center';
  const currentFontSize = selectedFormat.fontSize || '';

  const handleAlignChange = useCallback((align) => {
    if (!selectedCell) return;
    dispatch(updateCellFormat({ cellId: selectedCell, format: { align } }));
  }, [dispatch, selectedCell]);

  const handleFontSizeChange = useCallback((e) => {
    if (!selectedCell) return;
    const val = e.target.value;
    dispatch(updateCellFormat({
      cellId: selectedCell,
      format: { fontSize: val === '' ? undefined : Number(val) },
    }));
  }, [dispatch, selectedCell]);

  // ─── Panel sürükleme ──────────────────────────────
  const ref = useRef(null);
  const headerRef = useRef(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ width: 0, height: 0 });
  const [collapsed, setCollapsed] = useState(true);
  const [panelHeight] = useState(400);
  const TOPBAR_HEIGHT = 115;
  const SNAP_THRESHOLD = 30;
  const { registerPanel, updatePanelPosition, panels, setPanelHeight, getStackTop, activeTabId } = usePanels();
  const wasDragged = useRef(false);
  const toggle = () => setCollapsed((prev) => !prev);

  const showSnapHighlight = () => {
    const el = ref.current;
    if (!el) return;
    el.classList.add('snap-highlight');
    setTimeout(() => el.classList.remove('snap-highlight'), 300);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    registerPanel('BaslikPanel', { top: rect.top, left: rect.left, width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      updatePanelPosition('BaslikPanel', { top: rect.top, left: rect.left, width: rect.width, height: rect.height, collapsed });
      setPanelHeight('BaslikPanel', collapsed ? 40 : rect.height);
    });
  }, [collapsed]);

  // RESTORE — yalnız PAFTA değişince: o paftaya ait konum + açık/kapalı durumunu
  // geri yükle (sürüklenmişse kayıtlı konum, değilse stack; collapsed kayıtlıysa o,
  // yoksa varsayılan kapalı). getStackTop'a bağlı DEĞİL → açma/kapama bu effect'i
  // tetiklemez (aksi halde açınca konum ilk yerine kayıyordu).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stored = panels['BaslikPanel'];
    if (stored?.dragged) {
      el.style.left = `${stored.left}px`;
      el.style.top = `${stored.top}px`;
      wasDragged.current = true;
    } else {
      wasDragged.current = false;
      const stackTop = getStackTop('BaslikPanel');
      el.style.left = '';
      el.style.top = `${stackTop}px`;
      el.style.maxHeight = `${window.innerHeight - stackTop}px`;
    }
    const wantCollapsed = stored && typeof stored.collapsed === 'boolean' ? stored.collapsed : true;
    if (wantCollapsed !== collapsed) setCollapsed(wantCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Pafta-İÇİ yeniden stack (sürüklenmemiş paneller; başka panelin yüksekliği değişince)
  useEffect(() => {
    const el = ref.current;
    if (!el || wasDragged.current) return;
    const stackTop = getStackTop('BaslikPanel');
    el.style.top = `${stackTop}px`;
    el.style.maxHeight = `${window.innerHeight - stackTop}px`;
  }, [getStackTop]);

  useEffect(() => {
    const el = ref.current;
    const header = headerRef.current;
    const onMouseDown = (e) => {
      if (isResizing.current) return;
      isDragging.current = true;
      dragOffset.current = { x: e.clientX - el.offsetLeft, y: e.clientY - el.offsetTop };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      let x = e.clientX - dragOffset.current.x;
      let y = e.clientY - dragOffset.current.y;
      const ww = window.innerWidth;
      const w = el.offsetWidth;
      if (x < SNAP_THRESHOLD) { x = 0; showSnapHighlight(); }
      if (x + w > ww - SNAP_THRESHOLD) { x = ww - w; showSnapHighlight(); }
      if (y < TOPBAR_HEIGHT + SNAP_THRESHOLD) { y = TOPBAR_HEIGHT; showSnapHighlight(); }
      const h = el.offsetHeight;
      Object.entries(panels).forEach(([key, p]) => {
        if (key === 'BaslikPanel') return;
        if (Math.abs(x - (p.left + p.width)) < SNAP_THRESHOLD && Math.abs(y - p.top) < p.height) { x = p.left + p.width; y = p.top; showSnapHighlight(); }
        if (Math.abs(x + w - p.left) < SNAP_THRESHOLD && Math.abs(y - p.top) < p.height) { x = p.left - w; y = p.top; showSnapHighlight(); }
        if (Math.abs(y - (p.top + p.height)) < SNAP_THRESHOLD && Math.abs(x - p.left) < p.width) { y = p.top + p.height; x = p.left; showSnapHighlight(); }
        if (Math.abs(y + h - p.top) < SNAP_THRESHOLD && Math.abs(x - p.left) < p.width) { y = p.top - h; x = p.left; showSnapHighlight(); }
      });
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.maxHeight = `${window.innerHeight - y}px`;
      wasDragged.current = true;
      updatePanelPosition('BaslikPanel', { top: y, left: x, dragged: true });
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    header.addEventListener('mousedown', onMouseDown);
    return () => header.removeEventListener('mousedown', onMouseDown);
  }, [panels]);

  // ─── Resize ──────────────────────────────────────────
  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    const rect = ref.current.getBoundingClientRect();
    startSize.current = { width: rect.width, height: rect.height < panelHeight ? rect.height : panelHeight };
    document.addEventListener('mousemove', handleResizing);
    document.addEventListener('mouseup', stopResizing);
  };

  const handleResizing = (e) => {
    if (!isResizing.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    const newWidth = startSize.current.width + dx;
    const newHeight = startSize.current.height + dy;
    if (newWidth > 200) ref.current.style.width = `${newWidth}px`;
    if (newHeight < panelHeight) ref.current.style.height = `${newHeight}px`;
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResizing);
    document.removeEventListener('mouseup', stopResizing);
  };

  // ─── Render ────────────────────────────────────────
  return (
    <div ref={ref} className={`BaslikPanel ${collapsed ? 'collapsed' : ''}`} style={{ height: collapsed ? '40px' : undefined }}>
      <div className="BaslikPanel-header" ref={headerRef}>
        <span onClick={toggle}>Başlık Paneli</span>
        <span style={{ fontSize: '18px', cursor: 'pointer' }} onClick={toggle}>
          {collapsed ? '\u25BC' : '\u25B2'}
        </span>
      </div>

      <div className="BaslikPanel-content">
        {/* Sablon Secici */}
        <div className="baslik-template-selector">
          <div className="baslik-template-row">
            <label className="baslik-template-label">Sablon:</label>
            <select
              className="baslik-template-select"
              value={activeTemplateId || ''}
              onChange={handleSablonChange}
            >
              <option value="">Varsayilan</option>
              {dropdownTemplates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="baslik-template-actions">
            <button className="baslik-template-btn" onClick={handleYeniSablon}>Yeni Sablon</button>
            {activeTemplateId && (
              <>
                <button className="baslik-template-btn" onClick={handleSablonDuzenle}>Duzenle</button>
                <button className="baslik-template-btn baslik-template-btn-danger" onClick={handleSablonSil}>Sil</button>
                {activeTemplateId === defaultBaslikId ? (
                  <span className="baslik-template-default-badge" title="Tüm projelerde varsayılan">✓ Varsayılan</span>
                ) : (
                  <button className="baslik-template-btn" onClick={handleVarsayilanYap} title="Tüm projelerde varsayılan başlık yap">Varsayılan yap</button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Biçim Barı */}
        <div className={`baslik-format-bar ${!selectedCell ? 'disabled' : ''}`}>
          <label>Hiza:</label>
          <div className="baslik-align-group">
            <button className={`baslik-align-btn ${currentAlign === 'left' ? 'active' : ''}`} onClick={() => handleAlignChange('left')} title="Sol">&#8676;</button>
            <button className={`baslik-align-btn ${currentAlign === 'center' ? 'active' : ''}`} onClick={() => handleAlignChange('center')} title="Orta">&#8596;</button>
            <button className={`baslik-align-btn ${currentAlign === 'right' ? 'active' : ''}`} onClick={() => handleAlignChange('right')} title="Sağ">&#8677;</button>
          </div>
          <label>Boyut:</label>
          <input type="number" className="baslik-size-input" min={0.5} max={20} step={0.5}
            value={currentFontSize} onChange={handleFontSizeChange} placeholder="—" />
        </div>

        {/* Akordion Bölümler */}
        {BASLIK_SECTIONS.map((section) => {
          const isOpen = openSections[section.sectionKey] !== false;
          return (
            <div key={section.sectionKey}>
              <div className="baslik-section-header" onClick={() => toggleSection(section.sectionKey)}>
                <span className={`baslik-section-chevron ${isOpen ? 'open' : ''}`}>&#9654;</span>
                <span className="baslik-section-title">{section.title}</span>
              </div>
              <div className={`baslik-section-body ${isOpen ? '' : 'collapsed'}`}>
                {/* Bölüm başlığı */}
                {section.titleCellId && (
                  <div className={`baslik-title-row ${selectedCell === section.titleCellId ? 'baslik-cell-selected' : ''}`}
                    onClick={() => handleCellClick(section.titleCellId)}>
                    <span className="baslik-title-tag">Başlık</span>
                    <span className="baslik-title-text">{section.titleText}</span>
                  </div>
                )}

                {/* Items: label veya field */}
                {(section.items || []).map((item) => {
                  if (item.type === 'label') {
                    return (
                      <div key={item.cellId}
                        className={`baslik-label-row ${selectedCell === item.cellId ? 'baslik-cell-selected' : ''}`}
                        onClick={() => handleCellClick(item.cellId)}>
                        <span className="baslik-label-tag">T</span>
                        <span className="baslik-label-text">{item.text}</span>
                      </div>
                    );
                  }

                  // type === 'field'
                  const valueCellId = `${section.sectionKey}.${item.key}`;
                  const isLocked = cellLocks[valueCellId] !== false;
                  const value = (baslikDefaults[section.sectionKey] || {})[item.key] || '';

                  return (
                    <div key={valueCellId} className="baslik-field-row">
                      {/* Sol: etiket (tıkla → labelCellId seç) */}
                      <div
                        className={`baslik-field-label ${item.labelCellId && selectedCell === item.labelCellId ? 'selected' : ''}`}
                        onClick={() => handleCellClick(item.labelCellId || valueCellId)}
                        title={item.labelText || item.label}
                      >
                        {item.labelCellId && <span className="baslik-label-tag">T</span>}
                        <span className="baslik-field-label-text">
                          {item.labelText || item.label}
                        </span>
                      </div>

                      {/* Sağ: değer input + kilit (tıkla → valueCellId seç) */}
                      <div
                        className={`baslik-field-value ${selectedCell === valueCellId ? 'selected' : ''}`}
                        onClick={() => handleCellClick(valueCellId)}
                      >
                        <input
                          type="text"
                          className={isLocked ? 'baslik-locked' : ''}
                          value={value}
                          disabled={isLocked}
                          onChange={(e) => handleValueChange(section.sectionKey, item.key, e.target.value)}
                          onClick={() => handleCellClick(valueCellId)}
                        />
                        <button
                          className={`baslik-lock-btn ${isLocked ? 'locked' : ''}`}
                          onClick={(e) => handleLockToggle(e, valueCellId)}
                          title={isLocked ? 'Kilidi aç' : 'Kilitle'}
                        >
                          {isLocked ? '\uD83D\uDD12' : '\uD83D\uDD13'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="resize-handle" onMouseDown={handleResizeMouseDown} />
    </div>
  );
};

export default BaslikPanel;
